import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { CognitoIdentityServiceProvider } from 'aws-sdk';
import { Organization } from '../types/organization';
import { IdToken } from '../types/token';
import { User } from '../types/user';
import { Application } from '../types/application';

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  //  private readonly AUTH_RESULT_KEY = 'auth-result';
  private readonly ORGANIZATION_KEY = 'organization';
  private readonly ID_TOKEN_KEY = 'aws-cognito-id-token';
  private readonly ACCESS_TOKEN_KEY = 'aws-cognito-access-token';
  private readonly REFRESH_TOKEN_KEY = 'aws-cognito-refresh';
  private readonly APPLICATION_STATE_KEY = 'uux-state';
  private readonly UUX_SESSION_ID = 'uux-session';
  private readonly DEVICE_KEY = 'uuxDevice_';
  private readonly HISTORY_STATE_KEY = 'uuxHistory';
  private readonly ACCESS_RECORD_STATE_KEY = 'uuxRecordHistory';
  private readonly PRIVACY_NOTICE_KEY = 'mxprivacynotice';
  private readonly LOGGED_USER_KEY = 'username';
  private readonly UUX_PATIENT_CONTEXT="uux-patient-context";
  private readonly UUX_LOGOUT_URL="uux-logout-url";
  private readonly UUX_ACCESS_CODE="authorization_code";
  private readonly UUX_ORGANIZATION_ID="authorization_clientId";

  constructor() {}

  setRefreshTokenAuthResult(
    authResult: CognitoIdentityServiceProvider.AuthenticationResultType
  ) {
    sessionStorage.setItem(this.ID_TOKEN_KEY, authResult.IdToken);
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, authResult.AccessToken);
  }

  setAuthenticationResult(
    authResult: CognitoIdentityServiceProvider.AuthenticationResultType
  ) {
    sessionStorage.setItem(this.ID_TOKEN_KEY, authResult.IdToken);
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, authResult.AccessToken);
    sessionStorage.setItem(this.REFRESH_TOKEN_KEY, authResult.RefreshToken);
  }

  setAuthCode(authCode: string) {
    sessionStorage.setItem(this.UUX_ACCESS_CODE,authCode);
  }

  getAuthCode() {
    return sessionStorage.getItem(this.UUX_ACCESS_CODE);
  }

  setAuthClient(authClient: string) {
    sessionStorage.setItem(this.UUX_ORGANIZATION_ID,authClient);
  }

  getAuthClient() {
    return sessionStorage.getItem(this.UUX_ORGANIZATION_ID);
  }

  setLogoutURL(logoutUrl: string) {
    sessionStorage.setItem(this.UUX_LOGOUT_URL,logoutUrl);
  }

  getLogoutURL() {
    return sessionStorage.getItem(this.UUX_LOGOUT_URL);
  }

  setDeviceKey(username: string, deviceKey: string) {
    localStorage.setItem(this.DEVICE_KEY + _.toLower(username), deviceKey);
  }

  getDeviceKey(username: string): string {
    return localStorage.getItem(this.DEVICE_KEY + _.toLower(username));
  }

  setTemporaryDeviceKey(deviceKey: string) {
    sessionStorage.setItem(this.DEVICE_KEY, deviceKey);
  }

  getTemporaryDeviceKey(): string {
    return sessionStorage.getItem(this.DEVICE_KEY);
  }

  getUser(): User {
    return this.getIdToken().getUser();
  }

  getIdToken(): IdToken {
    return new IdToken(sessionStorage.getItem(this.ID_TOKEN_KEY));
  }

  getAccessToken(): string {
    return sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string {
    return sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getClientId(): string {
    return this.getOrganization().ClientId;
  }

  getOrganization(): Organization {
    return JSON.parse(sessionStorage.getItem(this.ORGANIZATION_KEY));
  }

  setOrganization(organization: Organization) {
    sessionStorage.setItem(this.ORGANIZATION_KEY, JSON.stringify(organization));
  }

  getUUXSessionId(): string {
    return sessionStorage.getItem(this.UUX_SESSION_ID);
  }

  setUUXSessionId(uuid: string) {
    sessionStorage.setItem(this.UUX_SESSION_ID, uuid);
  }

  getUUXPatientContext(): string {
    return sessionStorage.getItem(this.UUX_PATIENT_CONTEXT);
  }

  setUUXPatientContext(patientContext: string) {
    sessionStorage.setItem(this.UUX_PATIENT_CONTEXT, patientContext);
  }

  getPrivacyNoticeVersion(): string {
    return sessionStorage.getItem(this.PRIVACY_NOTICE_KEY);
  }

  setPrivacyNoticeVersion(version: string) {
    sessionStorage.setItem(this.PRIVACY_NOTICE_KEY, version);
  }
  getUserName(): string {
    return sessionStorage.getItem(this.LOGGED_USER_KEY);
  }

  setUserName(username: string) {
    sessionStorage.setItem(this.LOGGED_USER_KEY, username);
  }
  setApplication(app: Application) {
    sessionStorage.setItem(this.APPLICATION_STATE_KEY, JSON.stringify(app));
  }

  getApplication(): Application {
    const app = sessionStorage.getItem(this.APPLICATION_STATE_KEY);
    return app ? JSON.parse(app) : app;
  }

  setHistoryState() {
    // Needed to prevent false positives in Access for redirection
    sessionStorage.removeItem(this.ACCESS_RECORD_STATE_KEY);
    sessionStorage.setItem(
      this.HISTORY_STATE_KEY,
      // history length capped at 50
      Math.min(window.history.length, 49).toString()
    );
  }

  isAtHistoryEnd() {
    const hxState =
      parseInt(sessionStorage.getItem(this.HISTORY_STATE_KEY), 10) || 0;
    // If there is a history state and the current history length is greater
    // than that state, we are not at the end of the history
    return !(hxState && window.history.length > hxState);
  }
}
